import { Button, Skeleton, Stack } from '@mui/material';
import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetReportsQueryFilters,
  GetReportsQuerySort,
  Report,
  ReportStatuses,
  SORT_DIRECTION,
  useGetReportsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  buildFiltersSearchParams,
  clearLastAppliedFilter,
  StoredFilterSections,
} from '@schooly/components/filters';
import { DATE_FORMAT_SHORT } from '@schooly/constants';
import { LIST_ROW_COLUMNS } from '@schooly/constants/test-ids';
import {
  Counter,
  GridViewIcon,
  ListAccordion,
  ListAccordionHeader,
  ListRow,
  SectionBlock,
  TagSimple,
  TagSimpleColor,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { addDays, format } from 'date-fns';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useSchoolYears from '../../../../hooks/useSchoolYears';
import { EmptyState } from '../../TeacherDashboardContent';
import { useOpenMarkbookFromReport } from './useOpenMarkbookFromReport';
import { useCreatorLabel } from '../utils';

const REPORTS_WIDGET_PAGE_SIZE = 5;

type DueTag = 'overdue' | 'due-today' | 'due-tomorrow';

const TAG_LABEL_BY_KIND: Record<DueTag, string> = {
  overdue: 'dashboard-teacher-Reports-Overdue',
  'due-today': 'dashboard-teacher-Reports-DueToday',
  'due-tomorrow': 'dashboard-teacher-Reports-DueTomorrow',
};

const TAG_COLOR_BY_KIND: Record<DueTag, TagSimpleColor> = {
  overdue: 'error',
  'due-today': 'attention',
  'due-tomorrow': 'default',
};

const getDueTag = (scheduledPublishDate: string): DueTag | null => {
  const now = newDateTimezoneOffset();
  const todayStr = format(now, DEFAULT_DATE_FORMAT_FNS);
  const tomorrowStr = format(addDays(now, 1), DEFAULT_DATE_FORMAT_FNS);
  const dateStr = format(newDateTimezoneOffset(scheduledPublishDate), DEFAULT_DATE_FORMAT_FNS);

  if (dateStr < todayStr) return 'overdue';
  if (dateStr === todayStr) return 'due-today';
  if (dateStr === tomorrowStr) return 'due-tomorrow';
  return null;
};

export const ReportsWidget: FC = () => {
  const { schoolId = '', relationId } = useAuth();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { defaultValidity } = useSchoolYears();

  const { filters, sort } = useMemo(() => {
    const filters: GetReportsQueryFilters = {
      [FilterKeys.Staff]: [relationId ?? ''],
      [FilterKeys.ReportStatus]: [ReportStatuses.Unpublished],
      [FilterKeys.Date]: [defaultValidity?.start ?? '', defaultValidity?.end ?? ''],
    };

    const sort: GetReportsQuerySort[] = [
      { columnTextId: 'report_date', direction: SORT_DIRECTION.ASC },
    ];

    return { filters, sort };
  }, [defaultValidity?.end, defaultValidity?.start, relationId]);

  const { data, isLoading } = useGetReportsQuery(
    {
      school_id: schoolId,
      filters,
      sort,
      page_size: REPORTS_WIDGET_PAGE_SIZE,
    },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId && !!relationId && !!defaultValidity,
    },
  );

  const reports: Report[] = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data?.pages],
  );

  const total = data?.pages[0]?.count ?? 0;

  const hasMore = total > REPORTS_WIDGET_PAGE_SIZE;

  const handleShowAll = () => {
    clearLastAppliedFilter(StoredFilterSections.Reports);
    navigate(`/reports?${buildFiltersSearchParams(filters)}`);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" sx={{ mt: 2, minHeight: 140 }} />;
  }

  if (!total) {
    return (
      <SectionBlock withCard sx={{ mt: 2 }}>
        <EmptyState
          titleId="dashboard-teacher-EmptyReports-title"
          subtitleId="dashboard-teacher-EmptyReports-subtitle"
        />
      </SectionBlock>
    );
  }

  if (!total || !relationId) return null;

  return (
    <Stack mt={2.25}>
      <ListAccordion
        header={
          <ListAccordionHeader
            variant="attention"
            title={formatMessage({ id: 'dashboard-teacher-Reports-unpublished' })}
            counter={
              <Counter styleType="attentionSoft" size="sm">
                {total}
              </Counter>
            }
          />
        }
      >
        {reports.map((report) => (
          <ReportRow
            report={report}
            key={report.id}
            fallbackDate={[defaultValidity?.start ?? '', defaultValidity?.end ?? '']}
            relationId={relationId}
            onRowClick={() => navigate(`/reports/${report.id}`)}
          />
        ))}
        {hasMore && (
          <Stack alignItems="center" py={0.75}>
            <Button variant="text" onClick={handleShowAll}>
              <FormattedMessage id="action-ShowAll" />
            </Button>
          </Stack>
        )}
      </ListAccordion>
    </Stack>
  );
};

type ReportRowProps = {
  report: Report;
  fallbackDate: [string, string];
  relationId: string;
  onRowClick: () => void;
};

const ReportRow: FC<ReportRowProps> = ({ report, fallbackDate, relationId, onRowClick }) => {
  const { formatMessage } = useIntl();
  const { openMarkbook, isMarkbookOpening } = useOpenMarkbookFromReport(fallbackDate);
  const tag = report.scheduled_publish_date ? getDueTag(report.scheduled_publish_date) : null;
  const tagLabelId = tag ? TAG_LABEL_BY_KIND[tag] : null;
  const tagColor = tag ? TAG_COLOR_BY_KIND[tag] : 'default';

  const creatorLabel = useCreatorLabel(report.creator, relationId);

  return (
    <ListRow
      onClick={onRowClick}
      leadingContent={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.date}
          className="list-row-title-secondary"
          variant="h4"
          color="common.grey"
          ml={0.5}
        >
          {report.scheduled_publish_date
            ? format(newDateTimezoneOffset(report.scheduled_publish_date), DATE_FORMAT_SHORT)
            : ''}
        </TypographyWithOverflowHint>
      }
      subtitle={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.created_by}
          color="common.grey3"
          className="list-row-title-secondary"
        >
          {creatorLabel}
        </TypographyWithOverflowHint>
      }
      title={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.report_name}
          color="common.grey2"
          className="list-row-title"
          variant="h3"
        >
          {report.name}
        </TypographyWithOverflowHint>
      }
      rightContent={
        <Stack alignItems="flex-start" width={140}>
          {tagLabelId ? (
            <TagSimple data-test-id={LIST_ROW_COLUMNS.due_date} color={tagColor}>
              {formatMessage({ id: tagLabelId })}
            </TagSimple>
          ) : null}
        </Stack>
      }
      actions={
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openMarkbook(report.id);
          }}
          disabled={isMarkbookOpening}
          startIcon={<GridViewIcon />}
          sx={{ mr: 0.5 }}
        >
          <FormattedMessage id="dashboard-teacher-Reports-OpenMarkbook" />
        </Button>
      }
      sx={{ '.MuiCardActionArea-root': { height: 54, py: 0.5 } }}
    />
  );
};
